import React from 'react';
import {useQueries, useQuery} from '@tanstack/react-query';

import {getLocation} from 'common/util/url';
import {MetricResults, useExperimentsStore} from 'apps/experiments/src/store';
import {exposuresQueryKey, statsigQueryKey} from 'apps/experiments/src/react-query';
import {queryModule} from 'common/report/report-manager/query';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {RESULTS_OVERALL_SUM_KEY} from 'common/report/constants';
import {ExperimentMetricResults} from 'apps/experiments/src/types';
import {InsightsSeriesTree} from 'common/report/insights/util/types';
import {DEFAULT_CONTROL_KEY} from 'apps/experiments/src/components/utils';
import {
  createExposuresBookmarkParams,
  createStatsigBookmarkParams,
  getLatestSeriesValues,
  transformStatsigResults,
} from 'apps/experiments/src/components/query-utils';

type StoreMetricResultsParams = {
  projectId: number;
  workspaceId: number;
  experimentId: string;
};
export const storeMetricResults = (params: StoreMetricResultsParams) => {
  const experimentState = useExperimentsStore((state) => state.experimentState);
  const setMetricResults = useExperimentsStore((state) => state.setMetricResults);
  const setExposuresBookmarkParams = useExperimentsStore((state) => state.setExposuresBookmarkParams);
  const setExposuresQueryResults = useExperimentsStore((state) => state.setExposuresQueryResults);
  const {projectId, workspaceId, experimentId} = params;
  const {pathname, search, hash} = getLocation();

  const controlKey = experimentState?.settings.controlKey ?? DEFAULT_CONTROL_KEY;

  const exposuresBookmarkParams = React.useMemo(() => {
    if (!experimentState) {
      return undefined;
    }
    return createExposuresBookmarkParams({experiment: experimentState});
  }, [experimentState]);

  const {data: exposuresQueryResults = null, isSuccess: exposuresSuccess} = useQuery({
    queryKey: exposuresQueryKey({
      projectId: projectId!,
      workspaceId: workspaceId!,
      experimentId: experimentId!,
      bookmarkParams: exposuresBookmarkParams,
    }),
    enabled: !!exposuresBookmarkParams,
    queryFn: () => {
      const queryParams: {
        project_id: string;
        workspace_id?: string;
      } = {
        project_id: String(projectId),
      };
      if (workspaceId) {
        queryParams[`workspace_id`] = String(workspaceId);
      }
      return queryModule
        .executeReportQuery({
          endpoint: BookmarkType.Insights,
          queryParams,
          body: {
            bookmark: exposuresBookmarkParams,
            use_query_cache: true,
            use_query_sampling: false,
            report_query_origin: BookmarkType.Insights,
            tracking_props: {},
          },
          throwOnInvalidStatus: true,
          location: {
            pathname,
            search,
            hash,
            key: ``,
            state: ``,
          },
        })
        .then((response) => {
          return response.json();
        });
    },
  });

  const statsigBookmarkParams = React.useMemo(() => {
    const exposures = getLatestSeriesValues(exposuresQueryResults);
    if (!Object.values(exposures).length) {
      return [];
    }
    return (
      experimentState?.metrics.map((metric) =>
        createStatsigBookmarkParams({experiment: experimentState, metric, controlKey, exposures}),
      ) ?? []
    );
  }, [experimentState, exposuresQueryResults, controlKey]);

  const statsigQueryResults = useQueries({
    queries: statsigBookmarkParams.map((bookmarkParams) => {
      return {
        queryKey: statsigQueryKey({
          projectId: projectId!,
          workspaceId: workspaceId!,
          experimentId: experimentId!,
          bookmarkParams,
        }),
        enabled: exposuresSuccess && !!bookmarkParams,
        queryFn: async () => {
          const queryParams: {
            project_id: string;
            workspace_id?: string;
          } = {
            project_id: String(projectId),
          };
          if (workspaceId) {
            queryParams[`workspace_id`] = String(workspaceId);
          }
          try {
            const response = await queryModule.executeReportQuery({
              endpoint: BookmarkType.Insights,
              queryParams,
              body: {
                bookmark: bookmarkParams,
                use_query_cache: true,
                use_query_sampling: false,
                report_query_origin: BookmarkType.Insights,
                tracking_props: {},
              },
              throwOnInvalidStatus: true,
              location: {
                pathname,
                search,
                hash,
                key: ``,
                state: ``,
              },
            });
            return await response.json();
          } catch (error) {
            // We end up using the error message to determine what type of error tooltip
            // to show.  If it's an unknown error message (like one that even comes from
            // attempting to parse the error response json) we don't care because we have
            // a fallback error message and we are tracking all error messages in MixPanel
            const resp = await error.response.json();
            throw new Error(resp?.error);
          }
        },
      };
    }),
  });

  const nonTreatmentKeys = new Set([`undefined`, controlKey, RESULTS_OVERALL_SUM_KEY]);

  const metricResults: MetricResults = React.useMemo(() => {
    return statsigQueryResults.reduce((metricsMap, query, index) => {
      const metricId = experimentState?.metrics[index].id ?? ``;
      if (query.isSuccess) {
        const results = Object.values(query.data.series ?? {})[0];
        if (!results) {
          return metricsMap;
        }
        metricsMap[metricId] = Object.keys(results).reduce(
          (variantsMap, key) => {
            if (nonTreatmentKeys.has(key) || !statsigBookmarkParams[index]) {
              return variantsMap;
            }
            const statsigResults = transformStatsigResults({
              results: results as InsightsSeriesTree<number>,
              controlKey,
              treatmentKey: key,
            });
            if (!statsigResults) {
              return variantsMap;
            }
            variantsMap[key] = statsigResults;
            return variantsMap;
          },
          {} as Record<string, ExperimentMetricResults>,
        );
      } else if (query.isError) {
        metricsMap[metricId] = query.failureReason as Error;
      }
      return metricsMap;
    }, {} as MetricResults);
  }, [statsigQueryResults, experimentState?.metrics, controlKey]);

  React.useEffect(() => {
    setExposuresBookmarkParams(exposuresBookmarkParams);
    return () => {
      setExposuresBookmarkParams();
    };
  }, [exposuresBookmarkParams]);

  React.useEffect(() => {
    setExposuresQueryResults(exposuresQueryResults);
    return () => {
      setExposuresQueryResults();
    };
  }, [exposuresQueryResults]);

  React.useEffect(() => {
    setMetricResults(metricResults);
    return () => {
      setMetricResults({});
    };
  }, [metricResults]);
};

export const useFlattenedMetricResults = () => {
  const metricResults = useExperimentsStore((state) => state.metricResults);
  return React.useMemo(
    () =>
      Object.values(metricResults).reduce<ExperimentMetricResults[]>((acc, results) => {
        return [...acc, ...Object.values(results)];
      }, []),
    [metricResults],
  );
};

export const useVariants = () => {
  const metricResults = useExperimentsStore((state) => state.metricResults);
  return React.useMemo(
    () =>
      Object.values(metricResults).reduce((acc, result) => {
        return new Set([...acc, ...Object.keys(result)]);
      }, new Set<string>()),
    [metricResults],
  );
};
